/*
 * Copyright (c) 2026 Ambits Technologies LLC. All rights reserved.
 *
 * This source code is the proprietary and confidential property of
 * Ambits Technologies LLC. Unauthorized copying, modification, distribution,
 * or use of this software, via any medium, is strictly prohibited.
 *
 * MyAmbits - AI-Powered Location Safety & Social Platform
 * https://myambits.com
 */

import { useState, useEffect, useRef } from "react";
import {
  getCurrentPositionSafe,
  watchPositionSafe,
  type SafePositionOptions,
  type WatchHandle,
} from "@/utils/geolocation";

interface GeolocationState {
  lat: number | null;
  lng: number | null;
  loading: boolean;
  error: string | null;
  // True when lat/lng came from cached localStorage rather than a fresh fix.
  isFallback: boolean;
  // True when the hard timeout fired without a valid fresh fix arriving.
  timedOut: boolean;
}

const isNearZero = (lat: number, lng: number) =>
  Math.abs(lat) < 1 && Math.abs(lng) < 1;

const isBadCoord = (lat: number, lng: number) => isNearZero(lat, lng);

const GEO_OPTIONS: SafePositionOptions = {
  enableHighAccuracy: true,
  timeout: 15000,
  maximumAge: 0,
};

const MAX_RETRIES = 3;
const RETRY_INTERVAL = 3000;
// Hard ceiling: if no valid fix arrives within this window, stop the spinner
// and force coords to cached || US center so consumers never hang.
// Must be longer than GEO_OPTIONS.timeout so the underlying request gets a
// chance to complete; on iOS the user also needs time to tap "Allow" on the
// permission prompt, which can easily exceed 8s on a fresh install.
const HARD_TIMEOUT_MS = 20000;

// Geographic center of the contiguous United States — used as a safe fallback
// when no location and no cached fix is available.
const US_CENTER = { lat: 39.8283, lng: -98.5795 };

const CACHE_KEY = "aura-map:lastLocation";
const CACHE_TTL_MS = 24 * 60 * 60 * 1000;

// React-state throttle thresholds. We only flip the React `state` (which
// triggers re-renders) when the user has moved this far OR this much time
// has elapsed since the last setState. Live coords are still tracked in a
// ref every tick for callers that need them (e.g. RTDB presence writers).
const STATE_DISTANCE_THRESHOLD_M = 25;
const STATE_TIME_THRESHOLD_MS = 10_000;

interface CachedLocation {
  lat: number;
  lng: number;
  ts: number;
}

function readCache(): CachedLocation | null {
  try {
    const raw = typeof window !== "undefined" ? window.localStorage.getItem(CACHE_KEY) : null;
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CachedLocation;
    if (
      typeof parsed.lat !== "number" ||
      typeof parsed.lng !== "number" ||
      typeof parsed.ts !== "number" ||
      isBadCoord(parsed.lat, parsed.lng) ||
      Date.now() - parsed.ts > CACHE_TTL_MS
    ) {
      return null;
    }
    return parsed;
  } catch (err) {
    console.error("[useGeolocation] readCache failed", err);
    return null;
  }
}

function writeCache(lat: number, lng: number) {
  try {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(CACHE_KEY, JSON.stringify({ lat, lng, ts: Date.now() }));
  } catch (err) {
    // Quota / privacy errors are best-effort; log and continue.
    console.error("[useGeolocation] writeCache failed", err);
  }
}

function haversineMeters(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const toRad = (d: number) => (d * Math.PI) / 180;
  const R = 6_371_000;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

export interface LiveCoordsRef {
  current: { lat: number; lng: number; updatedAt: number } | null;
}

export function useGeolocation() {
  const [state, setState] = useState<GeolocationState>(() => {
    const cached = readCache();
    return {
      lat: cached?.lat ?? null,
      lng: cached?.lng ?? null,
      loading: true,
      error: null,
      isFallback: cached != null,
      timedOut: false,
    };
  });
  const retryCount = useRef(0);
  const gotValid = useRef(false);
  const retryTimer = useRef<ReturnType<typeof setTimeout>>();
  // Live coords: updated on every watchPosition tick. Reading this never
  // causes a re-render. Presence/RTDB writers should pull from here.
  const liveCoordsRef = useRef<{ lat: number; lng: number; updatedAt: number } | null>(null);
  // Last lat/lng/timestamp that was committed to React state. Used to decide
  // whether the next tick crosses the distance/time threshold.
  const lastCommittedRef = useRef<{ lat: number; lng: number; ts: number } | null>(null);

  useEffect(() => {
    const commitState = (lat: number, lng: number) => {
      lastCommittedRef.current = { lat, lng, ts: Date.now() };
      setState({ lat, lng, loading: false, error: null, isFallback: false, timedOut: false });
    };

    const accept = (lat: number, lng: number) => {
      if (isBadCoord(lat, lng)) {
        if (!gotValid.current && retryCount.current < MAX_RETRIES) {
          retryCount.current++;
          retryTimer.current = setTimeout(tryFresh, RETRY_INTERVAL);
        } else if (!gotValid.current && retryCount.current >= MAX_RETRIES) {
          setState(s => ({
            ...s,
            loading: false,
            error: "Unable to get accurate location. Please check your browser location settings.",
          }));
        }
        return;
      }

      // Always update the live ref + cache so presence writers and the
      // pre-paint cache see every tick at full GPS cadence.
      const now = Date.now();
      liveCoordsRef.current = { lat, lng, updatedAt: now };
      writeCache(lat, lng);

      // Throttle the React state commit. The first valid fix always commits
      // (so the UI exits "Getting your location…"). Subsequent ticks only
      // commit if the user moved >25m OR >10s elapsed since the last commit.
      if (!gotValid.current) {
        gotValid.current = true;
        commitState(lat, lng);
        return;
      }

      const last = lastCommittedRef.current;
      if (!last) {
        commitState(lat, lng);
        return;
      }
      const movedMeters = haversineMeters(last.lat, last.lng, lat, lng);
      const elapsedMs = now - last.ts;
      if (movedMeters >= STATE_DISTANCE_THRESHOLD_M || elapsedMs >= STATE_TIME_THRESHOLD_MS) {
        commitState(lat, lng);
      }
    };

    const onError = (err: { code: number; message: string }) => {
      console.error("[useGeolocation] position error", err);
      if (!gotValid.current) {
        if (retryCount.current < MAX_RETRIES) {
          retryCount.current++;
          retryTimer.current = setTimeout(tryFresh, RETRY_INTERVAL);
        } else {
          setState(s => ({ ...s, loading: false, error: err.message }));
        }
      }
    };

    const tryFresh = async () => {
      try {
        const pos = await getCurrentPositionSafe({ timeout: GEO_OPTIONS.timeout });
        accept(pos.lat, pos.lng);
      } catch (err) {
        const geoErr = err as GeolocationPositionError & { message?: string };
        onError({
          code: typeof geoErr?.code === "number" ? geoErr.code : 2,
          message: geoErr?.message ?? String(err),
        });
      }
    };

    // 1. Immediate fresh request
    tryFresh();

    // 2. Continuous watch for updates (also rejects bad coords)
    let watch: WatchHandle = { clear: () => {} };
    try {
      watch = watchPositionSafe(
        (pos) => accept(pos.coords.latitude, pos.coords.longitude),
        () => {},
        GEO_OPTIONS,
      );
    } catch (err) {
      console.error("[useGeolocation] watchPositionSafe threw", err);
    }

    // 3. Hard timeout: force a resolution so the UI never hangs. If no valid
    //    fix has arrived, fall back to the cached location or US center and
    //    flip loading=false regardless of outcome.
    const hardTimeout = setTimeout(() => {
      if (gotValid.current) return;
      const cached = readCache();
      console.warn("[useGeolocation] hard timeout fired, falling back", {
        hadCache: !!cached,
      });
      setState((s) => ({
        lat: s.lat ?? cached?.lat ?? US_CENTER.lat,
        lng: s.lng ?? cached?.lng ?? US_CENTER.lng,
        loading: false,
        error: s.error ?? "timeout",
        isFallback: true,
        timedOut: true,
      }));
    }, HARD_TIMEOUT_MS);

    // 4. Build 89 B5 — when the user returns from iOS Settings after
    //    granting location permission retroactively, the hook never knew
    //    and the "Location unavailable" banner stayed stuck. Listen for
    //    foreground transitions and re-run the first-fix request while we
    //    still lack a valid fix. Dynamic import so the web bundle doesn't
    //    pull in the native bridge.
    let cancelled = false;
    let appStateListener: { remove: () => void } | undefined;
    (async () => {
      try {
        const { App: CapApp } = await import("@capacitor/app");
        const handle = await CapApp.addListener("appStateChange", ({ isActive }) => {
          if (!isActive || gotValid.current || cancelled) return;
          retryCount.current = 0;
          setState((s) => ({ ...s, loading: true, error: null, timedOut: false }));
          void tryFresh();
        });
        if (cancelled) {
          handle.remove();
        } else {
          appStateListener = handle;
        }
      } catch (err) {
        // Web/test context — @capacitor/app isn't available. The browser
        // `visibilitychange` event is the closest analog but the
        // permission-denied → grant-in-OS-settings round trip is an
        // iOS-only flow, so no web-side listener is needed.
        console.debug("[useGeolocation] app-state listener unavailable:", err);
      }
    })();

    return () => {
      cancelled = true;
      appStateListener?.remove();
      watch.clear();
      if (retryTimer.current) clearTimeout(retryTimer.current);
      clearTimeout(hardTimeout);
    };
  }, []);

  return { ...state, liveCoordsRef };
}
